import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  closeArtifactPanel,
  exitArtifactFullscreen,
  openArtifactPanel,
  setArtifact,
  setArtifactVersions,
  showArtifactVersion,
  stopArtifactPolling,
  toggleArtifactFullscreen,
  versionRank,
} from "artifactsSlice";

import { setVisibleArtifactVersion } from "./artifactViewState";
import "./ArtifactPanel.css";

// CSP inyectada en el documento del iframe: sin red (connect-src
// 'none'), sin recursos externos salvo scripts/estilos de
// cdn.jsdelivr.net (Chart.js / Mermaid), imágenes sólo data:/blob:.
// DEBE mantenerse en sincronía con las reglas del prompt
// ARTIFACT_GENERATOR del backend (src/agentic/prompts.py) y con la
// página compartible /artifact/<id> (frontend app.py).
const ARTIFACT_CSP = [
  "default-src 'none'",
  "script-src 'unsafe-inline' https://cdn.jsdelivr.net",
  "style-src 'unsafe-inline' https://cdn.jsdelivr.net",
  "img-src data: blob:",
  "font-src data: https://cdn.jsdelivr.net",
  "connect-src 'none'",
  "form-action 'none'",
  "base-uri 'none'",
].join("; ");

// Si el backend nunca responde (evento perdido), el skeleton no debe
// quedarse para siempre: tras este tiempo se cierra en silencio.
const LOADING_SAFETY_TIMEOUT_MS = 4 * 60 * 1000;

// Límites del ancho del panel (drag para agrandar/achicar el split
// conversación-artifact).
const PANEL_MIN_WIDTH = 320;
const panelMaxWidth = () => Math.max(PANEL_MIN_WIDTH, window.innerWidth - 420);

// "Más reciente" dentro del catálogo de versiones: manda createdAt
// (con ramas, la versión nueva no es necesariamente la de número más
// alto) y el número de versión desempata si el documento no lo trae.
const isNewer = (candidate: any, current: any) => {
  const candidateDate = String(candidate?.createdAt || "");
  const currentDate = String(current?.createdAt || "");
  if (candidateDate !== currentDate) return candidateDate > currentDate;
  return versionRank(candidate?.version) > versionRank(current?.version);
};

// Los endpoints /api/artifact* requieren el JWT de la app (Entra ID).
const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("jwt");
  return token ? { Authorization: token } : {};
};

// El HTML del bot es un FRAGMENTO (sin <html>/<head>/<body>): se
// envuelve aquí en un documento completo con la CSP y un reset mínimo.
const wrapArtifactHtml = (html: string) => `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${ARTIFACT_CSP}">
<style>
  html, body { margin: 0; padding: 12px; background: #fff; color: #3d3d3d;
    font-family: "Segoe UI", Arial, sans-serif; box-sizing: border-box; }
  *, *::before, *::after { box-sizing: inherit; }
  img, svg, canvas { max-width: 100%; }
</style>
</head>
<body>
${html}
</body>
</html>`;

// Skeletons "ghost": 4 composiciones distintas (se elige una al azar
// por carga, ver artifactsSlice) que dan la ilusión de que el artifact
// está tomando forma en el panel. Sólo CSS (shimmer + float).
const ArtifactSkeleton = ({ variant }: { variant: number }) => {
  if (variant === 1) {
    // Gráfica: barras creciendo sobre un eje.
    return (
      <div className="artifact-skeleton" aria-hidden="true">
        <div className="skeleton-box skeleton-title" />
        <div className="skeleton-chart">
          {[52, 78, 38, 90, 64, 46, 82].map((height, i) => (
            <div
              key={i}
              className="skeleton-bar"
              style={{ height: `${height}%`, animationDelay: `${i * 0.12}s` }}
            />
          ))}
        </div>
        <div className="skeleton-box skeleton-line short" />
      </div>
    );
  }
  if (variant === 2) {
    // Diagrama: nodos conectados que "respiran".
    return (
      <div className="artifact-skeleton" aria-hidden="true">
        <div className="skeleton-box skeleton-title" />
        <div className="skeleton-diagram">
          <div className="skeleton-node top" />
          <div className="skeleton-connector" />
          <div className="skeleton-node-row">
            <div className="skeleton-node" style={{ animationDelay: "0.2s" }} />
            <div className="skeleton-node" style={{ animationDelay: "0.4s" }} />
            <div className="skeleton-node" style={{ animationDelay: "0.6s" }} />
          </div>
          <div className="skeleton-connector" />
          <div className="skeleton-node bottom" style={{ animationDelay: "0.8s" }} />
        </div>
      </div>
    );
  }
  if (variant === 3) {
    // Reporte: título, párrafos y una tabla.
    return (
      <div className="artifact-skeleton" aria-hidden="true">
        <div className="skeleton-box skeleton-title" />
        <div className="skeleton-box skeleton-line" />
        <div className="skeleton-box skeleton-line" />
        <div className="skeleton-box skeleton-line short" />
        <div className="skeleton-table">
          {[0, 1, 2, 3].map((row) => (
            <div key={row} className="skeleton-table-row" style={{ animationDelay: `${row * 0.15}s` }}>
              <div className="skeleton-box skeleton-cell" />
              <div className="skeleton-box skeleton-cell" />
              <div className="skeleton-box skeleton-cell" />
            </div>
          ))}
        </div>
        <div className="skeleton-box skeleton-line" />
        <div className="skeleton-box skeleton-line short" />
      </div>
    );
  }
  // Variante 0 (default): dashboard con tarjetas KPI y dos bloques.
  return (
    <div className="artifact-skeleton" aria-hidden="true">
      <div className="skeleton-box skeleton-title" />
      <div className="skeleton-cards">
        {[0, 1, 2].map((i) => (
          <div key={i} className="skeleton-box skeleton-card" style={{ animationDelay: `${i * 0.2}s` }} />
        ))}
      </div>
      <div className="skeleton-panels">
        <div className="skeleton-box skeleton-panel" />
        <div className="skeleton-box skeleton-panel" style={{ animationDelay: "0.3s" }} />
      </div>
      <div className="skeleton-box skeleton-line short" />
    </div>
  );
};

// Panel lateral del artifact interactivo (tool create_artifact del
// bot). Renderiza el HTML generado dentro de un iframe SANDBOXED:
// sólo allow-scripts — sin allow-same-origin el documento corre en un
// origen opaco y no puede leer cookies/localStorage (jwt, token de
// Direct Line) ni tocar el DOM de la app; sin allow-top-navigation ni
// allow-popups no puede navegar ni abrir ventanas.
export const ArtifactPanel = () => {
  const dispatch = useDispatch();
  const {
    sessionId,
    versions,
    htmlByArtifactId,
    title,
    html,
    artifactId,
    version,
    belongsToConversation,
    preferredArtifactId,
    isOpen,
    isFullscreen,
    isLoading,
    skeletonVariant,
    pollUntil,
  } = useSelector((state: any) => state.artifacts);
  const [linkCopied, setLinkCopied] = useState(false);
  const [navLoading, setNavLoading] = useState(false);
  // Ancho del panel (split redimensionable conversación-artifact).
  const [panelWidth, setPanelWidth] = useState<number>(() =>
    Math.min(Math.round(window.innerWidth * 0.42), 760)
  );
  const [isResizing, setIsResizing] = useState(false);
  // Sesión cuyo artifact ya se restauró (una sola vez por conversación)
  // y línea base de ids para el polling. Son refs y no estado porque el
  // intervalo del polling los consulta en cada tick sin re-crearse.
  const restoredSessionRef = useRef<string>("");
  const knownIdsRef = useRef<Set<string>>(new Set());
  const baselineReadyRef = useRef(false);

  const srcDoc = useMemo(() => (html ? wrapArtifactHtml(html) : ""), [html]);

  // Drag del borde izquierdo: mismo patrón que el resize del SideNav.
  useEffect(() => {
    if (!isResizing) return;
    const onMouseMove = (event: MouseEvent) => {
      const width = window.innerWidth - event.clientX;
      setPanelWidth(
        Math.min(Math.max(width, PANEL_MIN_WIDTH), panelMaxWidth())
      );
    };
    const onMouseUp = () => setIsResizing(false);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
  }, [isResizing]);

  // La versión visible se publica para que cada mensaje del usuario la
  // lleve como hint (channelData) al ARTIFACT_VERSION_SELECTOR. Sólo
  // aplica si el artifact es de la conversación en curso: el número de
  // versión de un artifact abierto desde la biblioteca (otra sesión) no
  // significa nada para el selector de esta conversación.
  useEffect(() => {
    setVisibleArtifactVersion(html && belongsToConversation ? version : null);
    return () => setVisibleArtifactVersion(null);
  }, [html, version, belongsToConversation]);

  // Esc sale de pantalla completa.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        dispatch(exitArtifactFullscreen());
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isFullscreen, dispatch]);

  // Red de seguridad del skeleton: si el evento final nunca llega,
  // se cierra como una generación fallida.
  useEffect(() => {
    if (!isLoading) return;
    const timeout = setTimeout(() => {
      dispatch(setArtifact({ html: "" }));
    }, LOADING_SAFETY_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [isLoading, dispatch]);

  // Catálogo de versiones: se refresca al conocer la sesión y cada vez
  // que llega una versión nueva (para numerar las flechas). La primera
  // carga de cada sesión además RESTAURA el artifact más reciente pero
  // SIN abrir el panel (restored: true): al retomar una conversación
  // sale sólo el botón y el usuario decide cuándo verlo.
  //
  // Excepción: si la conversación se abrió desde la biblioteca del
  // panel lateral, preferredArtifactId dice qué versión eligió el
  // usuario — esa se trae aunque no sea la última y el panel se abre.
  // No va en las dependencias a propósito: se fija en la MISMA acción
  // que la sesión (setArtifactSession), así que ya está en este render.
  useEffect(() => {
    if (!sessionId) {
      // Conversación nueva (clearArtifact): la siguiente sesión vuelve
      // a restaurar y a fijar su propia línea base de polling.
      restoredSessionRef.current = "";
      knownIdsRef.current = new Set();
      baselineReadyRef.current = false;
      return;
    }
    let cancelled = false;
    const firstLoad = restoredSessionRef.current !== sessionId;
    if (firstLoad) {
      restoredSessionRef.current = sessionId;
      knownIdsRef.current = new Set();
      baselineReadyRef.current = false;
    }
    // Un artifact ya en pantalla (recién generado en este turno, o la
    // versión que el usuario eligió con las flechas) no se pisa con el
    // más reciente del catálogo.
    const restore =
      firstLoad && (!!preferredArtifactId || (!html && !isLoading));
    (async () => {
      try {
        const response = await fetch(
          `/api/artifactVersions/${encodeURIComponent(sessionId)}`,
          { headers: authHeaders() }
        );
        const list = response.ok ? await response.json() : [];
        if (cancelled) return;
        const hasVersions = Array.isArray(list) && list.length > 0;
        if (hasVersions) {
          dispatch(setArtifactVersions(list));
          // Línea base del polling: todo lo que ya existía al entrar.
          list.forEach((item: any) => {
            if (item?.artifactId) knownIdsRef.current.add(item.artifactId);
          });
        }
        // Recién ahora el polling puede distinguir lo nuevo de lo que
        // ya estaba (si el catálogo falla se queda apagado: mejor eso
        // que abrir el panel solo con un artifact viejo).
        baselineReadyRef.current = true;
        if (!restore) return;
        // Versión elegida en la biblioteca (se trae aunque el catálogo
        // haya fallado) o, si no hay elección, la más reciente.
        const targetId =
          preferredArtifactId ||
          (hasVersions
            ? list.reduce((best: any, item: any) =>
                isNewer(item, best) ? item : best
              )?.artifactId
            : "");
        if (!targetId) return;
        const detail = await fetch(`/api/artifact/${targetId}`, {
          headers: authHeaders(),
        });
        if (cancelled || !detail.ok) return;
        // El artifact elegido se abre; el restore automático sólo deja
        // el botón (restored: true).
        dispatch(
          setArtifact({
            ...(await detail.json()),
            sessionId,
            restored: !preferredArtifactId,
          })
        );
      } catch {
        // Silencioso: sin catálogo el panel simplemente no ofrece
        // navegación entre versiones.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId, version, dispatch]);

  // Polling al RETOMAR una conversación: si había una generación en
  // curso al salir, la versión nueva aparece en cuanto se persista.
  // Detección por artifactId desconocido contra la línea base de
  // knownIdsRef (con ramas, la versión recién creada no es
  // necesariamente la de número más alto): entre los desconocidos se
  // muestra el de createdAt más reciente. Ese sí abre el panel: es un
  // artifact que el usuario pidió y todavía no ha visto.
  useEffect(() => {
    if (!sessionId || !pollUntil || Date.now() >= pollUntil) return;
    const interval = setInterval(async () => {
      if (Date.now() >= pollUntil) {
        dispatch(stopArtifactPolling());
        return;
      }
      // Sin línea base no hay forma de saber qué es nuevo.
      if (!baselineReadyRef.current) return;
      try {
        const response = await fetch(
          `/api/artifactVersions/${encodeURIComponent(sessionId)}`,
          { headers: authHeaders() }
        );
        if (!response.ok) return;
        const list = await response.json();
        if (!Array.isArray(list) || !list.length) return;
        dispatch(setArtifactVersions(list));
        const unknown = list.filter(
          (item: any) =>
            item?.artifactId && !knownIdsRef.current.has(item.artifactId)
        );
        if (unknown.length) {
          const newest = unknown.reduce((best: any, item: any) =>
            isNewer(item, best) ? item : best
          );
          knownIdsRef.current.add(newest.artifactId);
          // Versión recién generada: se descarga y se muestra (esto
          // además cierra la ventana de polling en el slice).
          const detail = await fetch(`/api/artifact/${newest.artifactId}`, {
            headers: authHeaders(),
          });
          if (detail.ok) {
            dispatch(setArtifact(await detail.json()));
          }
        }
      } catch {
        // Silencioso: el polling es best-effort.
      }
    }, 8000);
    return () => clearInterval(interval);
  }, [sessionId, pollUntil, dispatch]);

  const currentIndex = versions.findIndex(
    (item: any) => item.version === version
  );

  const goToVersion = async (target: any) => {
    if (!target?.artifactId || navLoading) return;
    const cached = htmlByArtifactId[target.artifactId];
    if (cached) {
      dispatch(showArtifactVersion({ ...target, html: cached }));
      return;
    }
    setNavLoading(true);
    try {
      const response = await fetch(`/api/artifact/${target.artifactId}`, {
        headers: authHeaders(),
      });
      if (response.ok) {
        dispatch(showArtifactVersion(await response.json()));
      }
    } catch {
      // Sin red / versión no disponible: se conserva la vista actual.
    } finally {
      setNavLoading(false);
    }
  };

  useEffect(() => {
    if (!linkCopied) return;
    const timeout = setTimeout(() => setLinkCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [linkCopied]);

  const copyShareLink = async () => {
    const shareUrl = `${window.location.origin}/artifact/${artifactId}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
    } catch {
      // Fallback para contextos sin Clipboard API (http, iframes).
      const textarea = document.createElement("textarea");
      textarea.value = shareUrl;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        setLinkCopied(true);
      } finally {
        document.body.removeChild(textarea);
      }
    }
  };

  if (!html && !isLoading) {
    return null;
  }

  if (!isOpen) {
    // El botón flotante para reabrir sólo tiene sentido si el artifact
    // es de la conversación en curso. En una conversación nueva, en una
    // sin artifacts o después de cerrar uno abierto desde la biblioteca
    // del panel lateral no queda nada colgado en pantalla: esos se
    // vuelven a abrir desde la sección "Artifacts".
    if (!belongsToConversation) {
      return null;
    }
    return (
      <button
        className="artifact-reopen-button"
        title={title}
        onClick={() => dispatch(openArtifactPanel())}
      >
        ◧ {title || "Artifact"}
      </button>
    );
  }

  return (
    <aside
      className={
        "artifact-panel" + (isFullscreen ? " artifact-panel--fullscreen" : "")
      }
      style={{ ["--artifact-panel-width" as any]: `${panelWidth}px` }}
    >
      {!isFullscreen && (
        <div
          className="artifact-resize-handle"
          title="Drag to resize"
          onMouseDown={(event) => {
            event.preventDefault();
            setIsResizing(true);
          }}
        />
      )}
      <div className="artifact-panel-header">
        <span className="artifact-panel-title" title={title}>
          {isLoading ? "Creating your artifact..." : title || "Artifact"}
        </span>
        {!isLoading && versions.length > 1 && (() => {
          const navIndex =
            currentIndex >= 0 ? currentIndex : versions.length - 1;
          return (
            <div className="artifact-version-nav">
              <button
                className="artifact-panel-action artifact-version-arrow"
                aria-label="Previous version"
                title="Previous version"
                disabled={navIndex <= 0 || navLoading}
                onClick={() => goToVersion(versions[navIndex - 1])}
              >
                ◀
              </button>
              <span className="artifact-version-label">
                {navLoading
                  ? "Loading..."
                  : `Versión ${version ?? versions[navIndex]?.version ?? "?"}`}
              </span>
              <button
                className="artifact-panel-action artifact-version-arrow"
                aria-label="Next version"
                title="Next version"
                disabled={navIndex >= versions.length - 1 || navLoading}
                onClick={() => goToVersion(versions[navIndex + 1])}
              >
                ▶
              </button>
            </div>
          );
        })()}
        <div className="artifact-panel-actions">
          {!isLoading && !!artifactId && (
            <button
              className="artifact-panel-action"
              aria-label="Copy link to this version"
              title="Copy link to this version"
              onClick={copyShareLink}
            >
              {linkCopied ? "✓ Copied" : "🔗 Share"}
            </button>
          )}
          {!isLoading && (
            <button
              className="artifact-panel-action"
              aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
              title={isFullscreen ? "Exit full screen (Esc)" : "Full screen"}
              onClick={() => dispatch(toggleArtifactFullscreen())}
            >
              {isFullscreen ? "🗗" : "⛶"}
            </button>
          )}
          <button
            className="artifact-panel-action artifact-panel-close"
            aria-label="Close artifact panel"
            onClick={() => dispatch(closeArtifactPanel())}
          >
            ✕
          </button>
        </div>
      </div>
      {isLoading && !html ? (
        <div className="artifact-panel-body">
          <ArtifactSkeleton variant={skeletonVariant} />
          <p className="artifact-skeleton-hint">
            Your artifact will appear here...
          </p>
        </div>
      ) : (
        <iframe
          className="artifact-panel-frame"
          title={title || "Luca artifact"}
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          srcDoc={srcDoc}
        />
      )}
      <footer className="artifact-panel-footer">
        <span className="policy-text">
          Always remember to use LUCA Bot in a responsable manner. For
          more information, please review our{" "}
          <a
            target="_blank"
            href="https://cemex.sharepoint.com/sites/ProcessIT/SitePages/Artificial-Intelligence-Policy.aspx"
            className="policy-link"
          >
            Cemex AI policy
          </a>
          . Artifact generated by Luca Bot.
        </span>
      </footer>
    </aside>
  );
};

export default ArtifactPanel;
